import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SessionService, User } from '../shared/service/session.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { Permissions } from '@app/shared/models/enums/permissions';
import { default as jwtDecode } from 'jwt-decode';
import { StorefrontService } from '../admin/service/storefront.service';
import { Customer } from '@app/shared/customer';

const baseUri = environment.apiBaseUrl.endsWith('/')
  ? environment.apiBaseUrl.slice(0, -1)
  : environment.apiBaseUrl;

enum ClaimTypes {
  email = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
  userId = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',
  name = 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
  scope = 'https://api-store.boostability.com/claims/scope',
  storefront = 'https://api-store.boostability.com/claims/storefront',
  clientData = 'https://api-store.boostability.com/claims/clientdata',
  returnUrl = 'https://api-store.boostability.com/claims/returnurl',
  catalog = 'https://api-store.boostability.com/claims/catalog',
  customer = 'https://api-store.boostability.com/claims/customer',
  allStorefronts = 'https://api-store.boostability.com/claims/storefront:all',
  allCatalogs = 'https://api-store.boostability.com/claims/catalog:all',
}

interface AuthToken {
  exp: number;
  iss: string;
  aud: string;
  // customer + partner claims
  [ClaimTypes.email]: string;
  // single scope appears in claim as string, multiple appear as string[]
  [ClaimTypes.scope]: Permissions[] | Permissions;
  [ClaimTypes.storefront]?: string;
  [ClaimTypes.clientData]?: string;
  [ClaimTypes.returnUrl]?: string;
  [ClaimTypes.catalog]?: string;
  [ClaimTypes.customer]?: number;
  // 'admin' claims, contains empty string, exists only when allowed
  [ClaimTypes.allCatalogs]?: string;
  [ClaimTypes.allStorefronts]?: string;
}

@Injectable({
  providedIn: 'root'
})
@Injectable()
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private permissionsService: NgxPermissionsService,
    private storefrontService: StorefrontService
  ) { }

  exchange(exchangeToken: string): Observable<string> {
    const url = `${baseUri}/sso/exchange?token=${exchangeToken}`;
    return this.http.get(url, { responseType: 'text' });
  }

  login(jwt: string): Observable<any> {
    const user = this.buildUser(jwt);
    this.permissionsService.loadPermissions(user.permissions);
    const storefrontId = user.storefrontId || this.sessionService.storefrontId;
    if (storefrontId) {
      return this.storefrontService.getStorefront(storefrontId).pipe(
        tap(sf => this.sessionService.start(user, sf))
      );
    }
    return of(this.sessionService.start(user));
  }

  logout() {
    this.permissionsService.flushPermissions();
    this.sessionService.end();
  }

  isTokenExpired(jwt: string): boolean {
    if (!jwt) {
      return true;
    }
    const decoded = jwtDecode<{ exp: number }>(jwt);
    const expired = new Date(decoded.exp * 1000) < new Date();
    return expired;
  }

  private buildUser(jwt: string): User {
    const decoded = jwtDecode<AuthToken>(jwt);
    const user: User = {
      userId: decoded[ClaimTypes.userId],
      fullName: decoded[ClaimTypes.name],
      email: decoded[ClaimTypes.email],
      permissions: this.getPermissions(decoded),
      returnUrl: decoded[ClaimTypes.returnUrl]
    };
    const clientData = decoded[ClaimTypes.clientData];
    if (clientData) {
      const customer: Customer = JSON.parse(clientData);
      user.customer = customer;
      user.storefrontId = decoded[ClaimTypes.storefront];
    }
    return user;
  }

  private getPermissions(token: AuthToken): Permissions[] {
    const permissions: Permissions[] = [].concat(token[ClaimTypes.scope]);
    const canChangeStorefront = permissions.includes(Permissions.managementWriteLimited)
      || permissions.includes(Permissions.managementWrite)
      || permissions.includes(Permissions.storefrontOnboarding);
    if (canChangeStorefront) {
      permissions.push(Permissions.viewAllStorefronts);
    }
    return permissions;
  }
}
